import type { JSX } from 'react';
import { useDeliveryOffer } from '../hooks/queries';
import { money } from '../lib/format';
import { useBasket } from '../state/basket-provider';
import { useCheckoutDraft } from '../state/checkout';
import { useLocale } from '../state/locale';
import { useSession } from '../state/session';

/**
 * GST-044: the delivery grid, as the line under the address states it
 * (founder, 2026-09-21).
 *
 *   basket empty            Free delivery above 70.00
 *   basket holds anything   Add 12.40 to save 5.65 on your delivery fees.
 *                           Free delivery above 70.00.        (green box)
 *
 * The saving used to sit under the delivery row of the review, in a green box;
 * it is HERE now and only here, on every screen, from the first item on - in
 * that same green. It is on the DELIVERY fees alone, plus a small-order fee
 * the basket pays now and the step would lift; never the service fee.
 *
 * Every figure is the server's (`useDeliveryOffer`, priced on the quote's own
 * courier question); this component formats and derives nothing. With no
 * saving to state - past the last step, a basket nobody can serve - the line
 * is the free-delivery basket alone, as it is before the first item.
 *
 * A stay states it too, and states it PER ORDER (founder, 2026-09-21): every
 * morning is its own order with the same basket, so the sentence is the first
 * morning's - what adding to the basket saves on each delivery - and never the
 * delivery fees of every morning added up.
 *
 * no-eternal-spinner: this line draws no spinner at all. Until the offer
 * answers, and if it never does, it states the session's own free-delivery
 * basket - the line it was before the first item - so a stalled or failed read
 * leaves a true sentence on screen, never a wheel and never an error.
 */
export function DeliveryOfferLine(): JSX.Element | null {
  const { locale, pt } = useLocale();
  const { session } = useSession();
  const { basket } = useBasket();
  const { draft } = useCheckoutDraft();

  /* The morning the draft holds - a stay's first - else the next one the
     session names: the server asks about the bag leaving as soon as it can
     when no time is set. */
  const serviceDay = draft.serviceDay ?? session?.nextServiceDay ?? null;
  const offer = useDeliveryOffer(
    basket,
    serviceDay,
    draft.serviceDay === null ? null : draft.windowStart,
    draft.soonest === true,
  );

  if (session === undefined || session === null) return null;
  const amount = (minor: number): string => money(minor, session.currency, locale);
  /* The session's figure until the offer answers, and a worker older than the
     field (`== null`) states nothing rather than a guess. */
  const freeFrom = offer.data?.freeDeliveryFrom ?? session.freeDeliveryFrom ?? null;
  const incentive = basket.lines.length === 0 ? null : (offer.data?.incentive ?? null);

  if (incentive !== null) {
    return (
      <span className="gw-appbar__offer gw-appbar__offer--saving" role="status">
        {freeFrom === null
          ? pt('price.addToSave', {
              amount: amount(incentive.addToBasket),
              saving: amount(incentive.saving),
            })
          : pt('price.addToSaveFreeAbove', {
              amount: amount(incentive.addToBasket),
              saving: amount(incentive.saving),
              free: amount(freeFrom),
            })}
      </span>
    );
  }
  if (freeFrom === null) return null;
  return (
    <span className="gw-appbar__offer">
      {pt('checkout.freeDeliveryAbove', { amount: amount(freeFrom) })}
    </span>
  );
}
